import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Select } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { checkingAccountService } from '@services/checkingAccountService';
import companyService from '@services/companyService';
import { FinancialAccount, formatAccountBankInfo } from '@/types/financial';
import { Company, formatCnpj } from '@/types/company';
import { formatCurrencyBRL } from '@pages/financial/financialUtils';

export const CheckingAccountListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<FinancialAccount[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyFilter, setCompanyFilter] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    companyService
      .getCompanies()
      .then(setCompanies)
      .catch(() => {
        /* filtro de empresa é opcional — silencia */
      });
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      setItems(await checkingAccountService.getAll(companyFilter ?? undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar contas correntes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [companyFilter]);

  const handleDelete = async (id: number) => {
    try {
      await checkingAccountService.delete(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const companyLabel = (a: FinancialAccount): string => {
    const doc = a.companyDocument ? `${formatCnpj(a.companyDocument)} — ` : '';
    return `${doc}${a.companyName ?? `Empresa ${a.companyId}`}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Conta Corrente</h1>
          <p className="text-slate-600">Cadastro de contas correntes das empresas do grupo</p>
        </div>
        <PermissionGuard permission="financial.accounts.write">
          <Button onClick={() => navigate('/settings/financial/checking-accounts/new')} icon={<Plus size={20} />}>
            Nova Conta
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtro por empresa */}
      <Card>
        <CardContent>
          <div className="max-w-md">
            <Select
              label="Empresa"
              value={companyFilter ?? ''}
              onChange={(e) => setCompanyFilter(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">Todas as empresas</option>
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {formatCnpj(c.document)} — {c.name}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">Empresa</th>
                <th className="text-left">Descrição</th>
                <th className="text-left">Banco</th>
                <th className="text-left">Agência / Conta</th>
                <th className="text-right">Saldo</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(6)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-slate-200 rounded w-24 animate-pulse" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    Nenhuma conta corrente encontrada
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.accountId}>
                    <TableCell className="text-sm">{companyLabel(item)}</TableCell>
                    <TableCell className="font-medium">{item.accountsDescription}</TableCell>
                    <TableCell className="text-sm">{item.bankName ?? item.bankNumber ?? '—'}</TableCell>
                    <TableCell className="text-sm">{formatAccountBankInfo(item) || '—'}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrencyBRL(item.currentBalance)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="financial.accounts.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/settings/financial/checking-accounts/${item.accountId}/edit`)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="financial.accounts.write">
                          {deleteConfirm === item.accountId ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.accountId)}>
                                Confirmar
                              </Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.accountId)}
                              icon={<Trash2 size={14} />}
                            >
                              Excluir
                            </Button>
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
